// API client. Every write call auto-attaches the current Cognito ID token.
// On 401 for an authenticated call, we try one silent token refresh and one
// retry; if that also fails, we sign out and bounce the user to /signin so
// they don't get stuck on a blank/broken screen with a stale token.
import { CONFIG } from "../config";
import { idToken, forceRefreshTokens, signOut } from "./auth";
import { getCurrentWorkspaceId } from "./workspace";

export interface EvidenceRow {
  evidenceId: string;
  ownerId: string;
  filename: string;
  mimeType: string;
  size: number;
  visibility: "public" | "private";
  status: "pending" | "uploaded" | "finalising" | "finalised" | "failed" | "deleted";
  label?: string | null;
  createdAt: string;
  updatedAt: string;
  uploadedAt?: string;
  finalisedAt?: string;
  manifestHash?: string;
  sha256?: string;
  declaredSha256?: string;
  immutableFileKey?: string;
  immutableManifestKey?: string;
  otsState?: "pending" | "anchored";
  otsCalendarsAccepted?: number;
  otsBitcoinBlockHeight?: number;
  otsBitcoinBlockHash?: string;
  // Bitcoin block mining time as a Unix epoch in seconds (UTC).
  otsBitcoinBlockTime?: number;
  otsConfirmedAt?: string;
  ledgerSeqNo?: number;
  ledgerRowHash?: string;
}

export interface InitBody {
  filename: string;
  mimeType: string;
  size: number;
  visibility: "public" | "private";
  declaredSha256?: string;
  label?: string;
}

export interface InitResult {
  evidenceId: string;
  presignedPutUrl: string;
  expiresIn: number;
  stagingKey: string;
}

export interface DownloadUrl {
  url: string;
  expiresAt: string;
  filename: string;
  contentType: string;
  size: number;
  disposition: "inline" | "attachment";
}

// Called when a session is unrecoverable. Redirects to /signin with a reason
// query param that SignIn.tsx picks up and displays as a friendly notice.
async function handleAuthLoss(reason: "expired" | "signed_out"): Promise<never> {
  try { await signOut(); } catch { /* ignore */ }
  const here = window.location.pathname + window.location.search;
  const params = new URLSearchParams({ reason });
  if (here && !here.startsWith("/signin")) params.set("from", here);
  window.location.replace(`/signin?${params.toString()}`);
  // Block the caller forever — the browser is about to navigate away, and we
  // don't want retry/error paths running against a torn-down session. Awaiting
  // a promise that never resolves gives this function a genuine `never` return
  // type without needing a cast.
  return new Promise<never>(() => { /* never resolves */ });
}

async function doFetch<T>(path: string, opts: RequestInit & { auth?: boolean }, token: string | null): Promise<{ ok: true; data: T } | { ok: false; status: number; data: unknown }> {
  const headers = new Headers(opts.headers);
  if (!headers.has("content-type") && opts.body) headers.set("content-type", "application/json");
  if (opts.auth && token) headers.set("authorization", `Bearer ${token}`);
  // M4 E4 — every authenticated request is scoped to a workspace. When the
  // header is absent (e.g. before the user has selected one), the backend
  // falls back to the caller's personal workspace, so this is safe to omit.
  if (opts.auth) {
    const wsId = getCurrentWorkspaceId();
    if (wsId) headers.set("x-workspace-id", wsId);
  }
  const r = await fetch(`${CONFIG.api}${path}`, { ...opts, headers });
  const text = await r.text();
  let data: unknown;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (r.ok) return { ok: true, data: data as T };
  return { ok: false, status: r.status, data };
}

async function req<T>(path: string, opts: RequestInit & { auth?: boolean } = {}): Promise<T> {
  let token: string | null = null;
  if (opts.auth) {
    token = await idToken();
    if (!token) { await handleAuthLoss("expired"); }
  }

  let result = await doFetch<T>(path, opts, token);

  // Retry once on 401 for authenticated calls: force-refresh the token and try again.
  // This handles the "sat idle past the 1h access token TTL" case without disturbing the user.
  if (!result.ok && result.status === 401 && opts.auth) {
    const refreshed = await forceRefreshTokens();
    if (!refreshed) { await handleAuthLoss("expired"); }
    result = await doFetch<T>(path, opts, refreshed);
    if (!result.ok && result.status === 401) { await handleAuthLoss("expired"); }
  }

  if (!result.ok) {
    const msg = typeof result.data === "object" && result.data && "message" in result.data
      ? String((result.data as { message: unknown }).message)
      : `HTTP ${result.status}`;
    throw new Error(msg);
  }
  return result.data;
}

export const api = {
  health: () => req<{ service: string; version: string; time: string }>("/health"),
  listMine: (opts?: { cursor?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.cursor) qs.set("cursor", opts.cursor);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    return req<{ items: EvidenceRow[]; nextCursor: string | null }>(`/evidence${suffix}`, { auth: true });
  },
  // Owner's detail-page fetch: full row, requires auth. Uses /mine, not the
  // public verifier route, because HTTP API v2's JWT authorizer isn't optional.
  get: (id: string) => req<EvidenceRow>(`/evidence/${encodeURIComponent(id)}/mine`, { auth: true }),
  // Public verifier fetch: no auth. Response is stripped for private items.
  getPublic: (id: string) => req<EvidenceRow>(`/evidence/${encodeURIComponent(id)}`),
  initUpload: (body: InitBody) => req<InitResult>("/uploads/init", {
    method: "POST", body: JSON.stringify(body), auth: true,
  }),
  completeUpload: (evidenceId: string) => req<{ executionArn?: string }>("/uploads/complete", {
    method: "POST", body: JSON.stringify({ evidenceId }), auth: true,
  }),
  getDownloadUrl: (id: string) =>
    req<DownloadUrl>(`/evidence/${encodeURIComponent(id)}/download`, { auth: true }),
  putLabel: (id: string, label: string | null) =>
    req<{ evidenceId: string; label: string | null; updatedAt: string }>(
      `/evidence/${encodeURIComponent(id)}/label`,
      { method: "PUT", body: JSON.stringify({ label }), auth: true },
    ),
  // Owner-only soft-delete. Marks the row deleted and stops the OTS-upgrade
  // Step Function if still awaiting Bitcoin confirmation. The physical file
  // stays in the immutable bucket under Object Lock until retention expires.
  deleteMine: (id: string) =>
    req<{ evidenceId: string; status: "deleted"; deletedAt: string; stoppedExecutionArn?: string | null }>(
      `/evidence/${encodeURIComponent(id)}/mine`,
      { method: "DELETE", auth: true },
    ),
};
